import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { adminColors, adminTypography } from '../../../../styles/adminDesignTokens';
import AdminSurface from '../../../../components/admin/AdminSurface';
import { useAddLocationDialog } from '../hooks/useAddLocationDialog';
import { useStoreLocationRegisters } from '../hooks/useStoreLocationRegisters';
import { useStoreLocations } from '../hooks/useStoreLocations';
import { useUpdateLocationDialog } from '../hooks/useUpdateLocationDialog';
import AddLocationDialog from './AddLocationDialog';
import StoreDetailsMapNewRegisterCard from './StoreDetailsMapNewRegisterCard';
import StoreDetailsLocationRegistersTable from './StoreDetailsLocationRegistersTable';
import StoreDetailsLocationsTable from './StoreDetailsLocationsTable';

type StoreDetailsStoreLocationsTabProps = {
  storeId: string;
};

const StoreDetailsStoreLocationsTab: React.FC<StoreDetailsStoreLocationsTabProps> = ({ storeId }) => {
  const { groupedLocations, loading, error, loadLocations, locations } = useStoreLocations(storeId);
  const {
    error: registersError,
    handleToggleActive,
    loading: registersLoading,
    loadRegisters,
    registers,
    updatingRegisterId,
  } = useStoreLocationRegisters(storeId);
  const addLocationDialog = useAddLocationDialog({
    storeId,
    onCreated: loadLocations,
    onRegistersRefreshed: loadRegisters,
  });
  const updateLocationDialog = useUpdateLocationDialog({
    storeId,
    onUpdated: loadLocations,
    onRegistersRefreshed: loadRegisters,
  });
  const isEditMode = updateLocationDialog.open;
  const isCreateMode = addLocationDialog.open;
  const activeDialogOpen = isEditMode || isCreateMode;

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          alignItems: 'start',
          gridTemplateColumns: { xs: '1fr', md: '1.08fr 0.92fr' },
        }}
      >
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Typography sx={{ ...adminTypography.sectionTitle, color: adminColors.text, fontSize: '20px' }}>
              Inventory Locations
            </Typography>
            <Button
              variant="contained"
              size="medium"
              onClick={addLocationDialog.handleOpen}
              sx={{
                backgroundColor: adminColors.primary,
                boxShadow: 'none',
                color: adminColors.surface,
                fontWeight: 600,
                height: 36,
                minWidth: 112,
                px: 1.5,
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: adminColors.primaryHover,
                  boxShadow: 'none',
                },
              }}
            >
              + Add Inventory Location
            </Button>
          </Box>
          <AdminSurface sx={{ overflow: 'hidden', p: 2 }}>
          <Box sx={{ display: 'grid', gap: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={34} />
              </Box>
            ) : error ? (
              <Box sx={{ display: 'grid', gap: 1 }}>
                <Typography sx={{ ...adminTypography.body, color: adminColors.danger }}>
                  {error}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={loadLocations}
                  sx={{
                    backgroundColor: adminColors.primary,
                    boxShadow: 'none',
                    width: 'fit-content',
                    '&:hover': { backgroundColor: adminColors.primaryHover },
                  }}
                >
                  Retry
                </Button>
              </Box>
            ) : groupedLocations.length > 0 ? (
              groupedLocations.map((group) => (
                <StoreDetailsLocationsTable
                  key={group.title}
                  title={group.title}
                  locations={group.items}
                  onEditLocation={(location) => updateLocationDialog.handleOpen({
                    id: location.id,
                    location_key: location.location_key,
                    location_name: location.location_name,
                    location_type: location.location_type,
                    vending_bin_count: location.vending_bin_count,
                  })}
                />
              ))
            ) : (
              <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText }}>
                No locations found for this store.
              </Typography>
            )}
          </Box>
          </AdminSurface>
        </Box>

        <Box
          sx={{
            alignSelf: 'start',
            display: 'grid',
            gap: 1.5,
            gridAutoRows: 'min-content',
          }}
        >
          <StoreDetailsLocationRegistersTable
            error={registersError}
            loading={registersLoading}
            locations={locations}
            onRetry={loadRegisters}
            onToggleActive={handleToggleActive}
            registers={registers}
            updatingRegisterId={updatingRegisterId}
          />

          <StoreDetailsMapNewRegisterCard
            storeId={storeId}
            locations={locations}
            onCreated={loadRegisters}
          />
        </Box>
      </Box>

      {activeDialogOpen ? (
        <AddLocationDialog
          mode={isEditMode ? 'edit' : 'create'}
          open={activeDialogOpen}
          error={isEditMode ? updateLocationDialog.error : addLocationDialog.error}
          locationKey={isEditMode ? updateLocationDialog.locationKey : addLocationDialog.locationKey}
          locationName={isEditMode ? updateLocationDialog.locationName : addLocationDialog.locationName}
          locationType={isEditMode ? updateLocationDialog.locationType : addLocationDialog.locationType}
          vendingBinCount={isEditMode ? updateLocationDialog.vendingBinCount : addLocationDialog.vendingBinCount}
          submitting={isEditMode ? updateLocationDialog.submitting : addLocationDialog.submitting}
          onClose={isEditMode ? updateLocationDialog.handleClose : addLocationDialog.handleClose}
          onLocationKeyChange={isEditMode ? (() => {}) : addLocationDialog.setLocationKey}
          onLocationNameChange={isEditMode ? updateLocationDialog.setLocationName : addLocationDialog.setLocationName}
          onLocationTypeChange={isEditMode ? (() => {}) : addLocationDialog.setLocationType}
          onVendingBinCountChange={isEditMode
            ? updateLocationDialog.setVendingBinCount
            : addLocationDialog.setVendingBinCount}
          onSubmit={isEditMode ? updateLocationDialog.handleSubmit : addLocationDialog.handleSubmit}
        />
      ) : null}
    </>
  );
};

export default React.memo(StoreDetailsStoreLocationsTab);
